import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Card, Chip, LinearProgress, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Tooltip, TextField, InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Add, Edit2, Trash, Link21 } from 'iconsax-react';
import { useObjectiveStore } from '../../store/objective.store';
import { useTaskStore } from '../../store/task.store';
import { useEmployeeStore } from '../../store/employee.store';
import { useAuthStore } from '../../store/auth.store';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { getApiErrorDetail } from '../../utils/taskApi.utils';
import { formatObjectiveAmount, objectiveTaskKey, objectiveTaskToTask } from '../../utils/objective.utils';
import { buildToggleCompleteWrite } from '../../utils/task.utils';
import { UpdateObjectiveRequest } from '../../types/objective.type';
import { Task } from '../../types/task.type';
import ObjectiveFormDialog from '../../components/objectives/ObjectiveFormDialog';
import LinkTaskDialog, { NewObjectiveLink } from '../../components/objectives/LinkTaskDialog';
import TaskRow from '../../components/tasks/TaskRow';
import TaskFormDialog from '../../components/tasks/TaskFormDialog';
import DeleteTaskDialog from '../../components/tasks/DeleteTaskDialog';

const LINKED_TASK_GRID = 'minmax(160px, 1.15fr) minmax(120px, 0.9fr) 120px 108px 96px 96px minmax(108px, auto)';

export default function ObjectiveDetailsPage() {
  const { objectiveId } = useParams<{ objectiveId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const {
    currentObjective,
    isDetailLoading,
    fetchObjective,
    editObjective,
    removeObjective,
    unlinkTask,
    saveTaskLinks,
    clearCurrentObjective,
  } = useObjectiveStore();
  const {
    tasks,
    isLoading: tasksLoading,
    fetchTasks,
    updateEmployeeTask,
    deleteEmployeeTask,
  } = useTaskStore();
  const { employees, fetchEmployees } = useEmployeeStore();
  const { user } = useAuthStore();
  const [formOpen, setFormOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [weightDraft, setWeightDraft] = useState<Record<string, string>>({});
  const [savingWeights, setSavingWeights] = useState(false);
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  useEffect(() => {
    if (!objectiveId) return;
    fetchObjective(objectiveId).catch(() => {
      showToast(t('objectives.notFound'), TOAST_TYPES.ERROR);
      navigate('/dashboard/objectives');
    });
    fetchEmployees();
    return () => clearCurrentObjective();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveId]);

  useEffect(() => {
    if (linkOpen) fetchTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkOpen]);

  const objective = currentObjective;
  const pct = objective ? Math.min(100, Math.max(0, objective.progress_percentage ?? 0)) : 0;

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const task of objective?.tasks ?? []) {
      const key = objectiveTaskKey(task.owner_id, task.task_list_id, task.google_task_id);
      next[key] = task.weight == null ? '' : String(task.weight);
    }
    setWeightDraft(next);
  }, [objective?.id, objective?.tasks]);
  const progressColor = pct >= 100 ? '#10B981' : pct >= 50 ? '#3B82F6' : '#F59E0B';

  const employeeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const emp of employees) map.set(emp.id, emp.name);
    if (user) map.set(user.id, user.name);
    return map;
  }, [employees, user]);

  const linkedTasks = useMemo(
    () => (objective?.tasks ?? []).map(t => objectiveTaskToTask(t, employeeNameMap)),
    [objective?.tasks, employeeNameMap],
  );

  const employeeOptionsForForm = useMemo(
    () => employees
      .filter(e => e.google_access_status === 'approved')
      .map(e => ({ id: e.id, name: e.name })),
    [employees],
  );

  const refreshObjective = async () => {
    if (objectiveId) await fetchObjective(objectiveId);
  };

  const handleEdit = async (data: UpdateObjectiveRequest) => {
    if (!objectiveId) return;
    try {
      await editObjective(objectiveId, data);
      showToast(t('objectives.updatedSuccess'), TOAST_TYPES.SUCCESS);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
      throw e;
    }
  };

  const handleDelete = async () => {
    if (!objectiveId) return;
    setDeleting(true);
    try {
      await removeObjective(objectiveId);
      showToast(t('objectives.deletedSuccess'), TOAST_TYPES.SUCCESS);
      navigate('/dashboard/objectives');
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const weightSum = (objective?.tasks ?? []).reduce((sum, task) => {
    const key = objectiveTaskKey(task.owner_id, task.task_list_id, task.google_task_id);
    const n = Number(weightDraft[key]);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  const handleSaveWeights = async () => {
    if (!objectiveId || !objective) return;
    if (Math.abs(weightSum - 100) > 0.001) {
      showToast(t('objectives.weightSumInvalid'), TOAST_TYPES.ERROR);
      return;
    }
    setSavingWeights(true);
    try {
      await saveTaskLinks(objectiveId, (objective.tasks ?? []).map(task => ({
        owner_id: task.owner_id,
        task_list_id: task.task_list_id,
        google_task_id: task.google_task_id,
        weight: Number(weightDraft[objectiveTaskKey(task.owner_id, task.task_list_id, task.google_task_id)]),
      })));
      showToast(t('objectives.weightsSaved'), TOAST_TYPES.SUCCESS);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setSavingWeights(false);
    }
  };

  const handleSaveNewLinks = async (links: NewObjectiveLink[]) => {
    if (!objectiveId || !objective) return;
    setSavingWeights(true);
    try {
      await saveTaskLinks(objectiveId, links.map(link => ({
        owner_id: link.owner_id,
        task_list_id: link.task_list_id,
        google_task_id: link.google_task_id,
        weight: link.weight,
      })));
      showToast(t('objectives.taskLinked'), TOAST_TYPES.SUCCESS);
      setLinkOpen(false);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
      throw e;
    } finally {
      setSavingWeights(false);
    }
  };

  const handleToggleComplete = async (task: Task) => {
    setMutatingId(task.google_task_id);
    try {
      await updateEmployeeTask(task.google_task_id, {
        employee_id: task.employee_id,
        task_list_id: task.task_list_id,
        ...buildToggleCompleteWrite(task),
      });
      await refreshObjective();
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setMutatingId(null);
    }
  };

  const handleTaskUpdated = async () => {
    showToast(t('tasks.taskUpdated'), TOAST_TYPES.SUCCESS);
    await refreshObjective();
  };

  const handleDeleteTaskConfirm = async () => {
    if (!deleteTarget) return;
    setDeletingTask(true);
    try {
      await deleteEmployeeTask(deleteTarget);
      if (objectiveId) {
        await unlinkTask(objectiveId, deleteTarget.employee_id, deleteTarget.task_list_id, deleteTarget.google_task_id).catch(() => {});
      }
      showToast(t('tasks.taskDeleted'), TOAST_TYPES.SUCCESS);
      setDeleteTarget(null);
      await refreshObjective();
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setDeletingTask(false);
    }
  };

  if (isDetailLoading && !objective) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!objective) return null;

  const columns = [
    t('tasks.tableTask'),
    t('tasks.tableSpace'),
    t('tasks.tableAssignee'),
    t('tasks.tableDueDate'),
    t('tasks.tableStatus'),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard/objectives')} size="small">
          <ArrowLeft size={20} color="#64748B" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.3px' }}>
            {objective.title}
          </Typography>
          {objective.team_name && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.25 }}>
              {objective.team_name}
            </Typography>
          )}
        </Box>
        <Tooltip title={t('common.edit')}>
          <IconButton
            onClick={() => setFormOpen(true)}
            sx={{ border: '1px solid #E2E8F0', borderRadius: 2, '&:hover': { bgcolor: '#EFF6FF' } }}
          >
            <Edit2 size={20} color="#64748B" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('common.delete')}>
          <IconButton
            onClick={() => setDeleteOpen(true)}
            sx={{ border: '1px solid #FECACA', borderRadius: 2, '&:hover': { bgcolor: '#FEF2F2' } }}
          >
            <Trash size={20} color="#EF4444" />
          </IconButton>
        </Tooltip>
      </Box>

      <GridCards objective={objective} locale={locale} t={t} pct={pct} progressColor={progressColor} />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, mt: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('objectives.linkedTasks')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.25 }}>
            {t('objectives.weightsHint')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {(objective.tasks ?? []).length > 0 && (
            <Box sx={{ minWidth: 148 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{t('objectives.weight')}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: Math.abs(weightSum - 100) < 0.001 ? '#16A34A' : '#D97706' }}>
                  {formatObjectiveAmount(weightSum)}/100
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, weightSum))}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': { bgcolor: Math.abs(weightSum - 100) < 0.001 ? '#16A34A' : '#F59E0B', borderRadius: 3 },
                }}
              />
            </Box>
          )}
          <Button
            variant="outlined"
            size="small"
            disabled={savingWeights || (objective.tasks ?? []).length === 0 || Math.abs(weightSum - 100) > 0.001}
            onClick={handleSaveWeights}
            sx={{ textTransform: 'none' }}
          >
            {savingWeights ? t('common.loading') : t('objectives.saveWeights')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add size={16} color="#fff" />}
            onClick={() => setLinkOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            {t('objectives.linkTask')}
          </Button>
        </Box>
      </Box>

      <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {!linkedTasks.length ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Link21 size={36} color="#CBD5E1" />
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 1.5 }}>
              {t('objectives.noLinkedTasks')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add size={16} color="#3B82F6" />}
              onClick={() => setLinkOpen(true)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              {t('objectives.linkTask')}
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: LINKED_TASK_GRID,
              px: 2.5, py: 1.25, gap: 2,
              borderBottom: '1px solid #F3F4F6',
              bgcolor: '#F9FAFB',
            }}>
              {columns.map(label => (
                <Typography key={label} sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                  {label}
                </Typography>
              ))}
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                {t('objectives.weight')}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textAlign: 'end' }}>
                {t('common.actions')}
              </Typography>
            </Box>
            {linkedTasks.map(task => (
              <TaskRow
                key={objectiveTaskKey(task.employee_id, task.task_list_id, task.google_task_id)}
                task={task}
                gridColumns={LINKED_TASK_GRID}
                actionsLoading={mutatingId === task.google_task_id}
                onToggleComplete={handleToggleComplete}
                onEdit={t => { setEditTask(t); setTaskFormOpen(true); }}
                onDelete={setDeleteTarget}
                onThreadReply={refreshObjective}
                beforeActions={
                  <TextField
                    size="small"
                    type="number"
                    value={weightDraft[objectiveTaskKey(task.employee_id, task.task_list_id, task.google_task_id)] ?? ''}
                    onChange={e => {
                      const key = objectiveTaskKey(task.employee_id, task.task_list_id, task.google_task_id);
                      setWeightDraft(prev => ({ ...prev, [key]: e.target.value }));
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    sx={{ width: 96, '& .MuiInputBase-input': { py: 0.75, px: 1 } }}
                  />
                }
              />
            ))}
          </>
        )}
      </Card>

      <ObjectiveFormDialog
        open={formOpen}
        objective={objective}
        onClose={() => setFormOpen(false)}
        onSubmit={handleEdit}
      />

      <LinkTaskDialog
        open={linkOpen}
        tasks={tasks}
        linkedTasks={objective.tasks ?? []}
        loading={tasksLoading}
        saving={savingWeights}
        onClose={() => setLinkOpen(false)}
        onSave={handleSaveNewLinks}
      />

      <TaskFormDialog
        open={taskFormOpen}
        mode="employee"
        task={editTask}
        employeeOptions={employeeOptionsForForm}
        defaultEmployeeId={editTask?.employee_id}
        onClose={() => { setTaskFormOpen(false); setEditTask(null); }}
        onSubmitEmployee={async () => {}}
        onSubmitMy={async () => {}}
        onSubmitEditEmployee={async (payload) => {
          if (!editTask) return;
          await updateEmployeeTask(editTask.google_task_id, payload);
          await handleTaskUpdated();
        }}
        onSubmitEditMy={async () => {}}
      />

      <DeleteTaskDialog
        open={!!deleteTarget}
        task={deleteTarget}
        loading={deletingTask}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteTaskConfirm}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('objectives.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('objectives.deleteConfirmBody')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function GridCards({
  objective,
  locale,
  t,
  pct,
  progressColor,
}: {
  objective: NonNullable<ReturnType<typeof useObjectiveStore.getState>['currentObjective']>;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  pct: number;
  progressColor: string;
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2.5 }}>
      <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          {t('objectives.progress')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: progressColor, lineHeight: 1 }}>
            {formatObjectiveAmount(objective.progress_percentage)}%
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {formatObjectiveAmount(objective.achieved_value)}/{formatObjectiveAmount(objective.target_value)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: '#E5E7EB',
            mb: 1,
            '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 5 },
          }}
        />
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          {t('objectives.tasksProgress', {
            completed: objective.tasks_completed,
            total: objective.tasks_total,
          })}
        </Typography>
        {objective.metric && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #F3F4F6' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              {t('objectives.fields.metric')}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
              {objective.metric}
            </Typography>
          </Box>
        )}
      </Card>

      <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <StackMeta objective={objective} locale={locale} t={t} />
      </Card>
    </Box>
  );
}

function StackMeta({
  objective,
  locale,
  t,
}: {
  objective: NonNullable<ReturnType<typeof useObjectiveStore.getState>['currentObjective']>;
  locale: string;
  t: (key: string) => string;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {objective.due_date && (
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
            {t('objectives.fields.dueDate')}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', mt: 0.25 }}>
            {new Date(objective.due_date).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
          {t('objectives.fields.owners')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
          {(objective.owners ?? []).map(owner => (
            <Chip
              key={`${owner.type}-${owner.id}`}
              avatar={
                <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem' }}>
                  {owner.name.charAt(0)}
                </Avatar>
              }
              label={owner.name}
              size="small"
              sx={{ height: 28, fontSize: '0.75rem' }}
            />
          ))}
          {(!objective.owners || objective.owners.length === 0) && (
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>—</Typography>
          )}
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
          {t('objectives.createdAt')}
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', mt: 0.25 }}>
          {new Date(objective.created_at).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </Typography>
      </Box>
    </Box>
  );
}
